import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import FloorImageDropZone from './FloorImageDropZone';

/**
 * Single draggable floor card. Uses @dnd-kit's useSortable so the
 * card can be reordered by dragging the grip handle on its left.
 *
 * Everything here is driven by props — the card never talks to the
 * backend directly. The parent (FloorManager) owns the floor list
 * and auto-save logic and just wires these callbacks up.
 */
const SortableFloorCard = ({
  floor,
  index,
  buf,
  saveState,
  onNameChange,
  onNameBlur,
  onImageChange,
  onDelete,
  onSetDefault,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: floor.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  const preview = buf.filePreview || floor.floor_image;

  let statusLabel = null;
  let statusClass = '';
  if (saveState === 'saving') {
    statusLabel = 'Saving…';
    statusClass = 'is-saving';
  } else if (saveState === 'saved') {
    statusLabel = 'Saved';
    statusClass = 'is-saved';
  } else if (saveState === 'error') {
    statusLabel = 'Save failed';
    statusClass = 'is-error';
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`wizard-floor-card ${isDragging ? 'is-dragging' : ''}`}
    >
      <button
        type="button"
        ref={setActivatorNodeRef}
        {...attributes}
        {...listeners}
        className="wizard-floor-drag-handle"
        title="Drag to reorder"
        aria-label={`Drag floor ${floor.name} to reorder`}
      >
        <span className="material-symbols-outlined">drag_indicator</span>
        <span className="wizard-floor-order-badge">#{index + 1}</span>
      </button>

      <button
        type="button"
        className="wizard-floor-delete-icon"
        title={`Delete floor "${buf.name || floor.name}"`}
        aria-label={`Delete floor "${buf.name || floor.name}"`}
        onClick={() => onDelete(floor)}
      >
        <span className="material-symbols-outlined">delete</span>
      </button>

      <FloorImageDropZone
        preview={preview}
        emptyLabel="Drop floor image here or click to browse"
        ariaLabel={`Replace image for ${buf.name || floor.name}`}
        onFileSelected={(file) => onImageChange(floor.id, file)}
      />

      <div className="wizard-floor-card-fields">
        <label>
          Name
          <input
            type="text"
            value={buf.name || ''}
            onChange={(e) => onNameChange(floor.id, e.target.value)}
            onBlur={() => onNameBlur?.(floor.id)}
          />
        </label>
        <label className="wizard-inline">
          <input
            type="radio"
            name="default-floor"
            checked={!!buf.is_default}
            onChange={onSetDefault}
          />
          Default
        </label>
      </div>

      {statusLabel && (
        <div className={`wizard-floor-save-chip ${statusClass}`} role="status">
          {statusLabel}
        </div>
      )}
    </div>
  );
};

export default SortableFloorCard;
